import imageCompression from 'browser-image-compression';

/**
 * Compress an image file before upload
 * @param {File} file - The image file to compress
 * @param {Object} options - Compression options
 * @param {number} options.maxSizeMB - Maximum file size in MB (default: 1)
 * @param {number} options.maxWidthOrHeight - Maximum width or height in pixels (default: 1920)
 * @param {number} options.quality - JPEG quality 0-1 (default: 0.8)
 * @returns {Promise<File>} - Compressed image file
 */
export const compressImage = async (file, options = {}) => {
  const {
    maxSizeMB = 1,
    maxWidthOrHeight = 1920,
    quality = 0.8,
  } = options;

  // Skip compression for small files (< 500KB) and non-image files
  if (!file.type.startsWith('image/') || file.size < 500 * 1024) {
    return file;
  }

  // Skip compression for GIFs (to preserve animation)
  if (file.type === 'image/gif') {
    return file;
  }

  try {
    const compressionOptions = {
      maxSizeMB,
      maxWidthOrHeight,
      useWebWorker: true,
      initialQuality: quality,
      // Preserve EXIF orientation
      exifOrientation: undefined,
    };

    const compressedFile = await imageCompression(file, compressionOptions);
    
    // Log compression results for debugging
    const originalSize = (file.size / 1024 / 1024).toFixed(2);
    const compressedSize = (compressedFile.size / 1024 / 1024).toFixed(2);
    const savings = ((1 - compressedFile.size / file.size) * 100).toFixed(1);
    
    console.log(`Image compressed: ${originalSize}MB → ${compressedSize}MB (${savings}% reduction)`);
    
    return compressedFile;
  } catch (error) {
    console.error('Image compression failed:', error);
    // Return original file if compression fails
    return file;
  }
};

/**
 * Compress image and convert to base64
 * @param {File} file - The image file
 * @param {Object} options - Compression options
 * @returns {Promise<string>} - Base64 encoded compressed image
 */
export const compressImageToBase64 = async (file, options = {}) => {
  const compressedFile = await compressImage(file, options);
  
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(compressedFile);
  });
};

/**
 * Preset configurations for different use cases
 */
export const compressionPresets = {
  // For profile avatars - smaller, square images
  avatar: {
    maxSizeMB: 0.5,
    maxWidthOrHeight: 512,
    quality: 0.85,
  },
  // For header/banner images - wider, larger
  header: {
    maxSizeMB: 1,
    maxWidthOrHeight: 1920,
    quality: 0.8,
  },
  // For content images - balance between quality and size
  content: {
    maxSizeMB: 1,
    maxWidthOrHeight: 1920,
    quality: 0.85,
  },
  // For thumbnails - very small
  thumbnail: {
    maxSizeMB: 0.2,
    maxWidthOrHeight: 400,
    quality: 0.75,
  },
};

export default compressImage;
